import * as swcHelpers from '@swc/helpers';
import React__default from 'react';
import { Hooks, cartIdVar } from '@grandvisionhq/carts-v2';
import { toast } from '@grandvisionhq/common';
import { useMutation } from '@grandvisionhq/graphql';
import { useDispatch } from '@grandvisionhq/state';
import { updateCartState } from '../../actions.js';
import { useMutationConfig } from './use-mutation-config.js';
import { useUpdateCartCache } from './use-update-cart-cache.js';
import { getFlattenedCartFailureErrorCodes } from '../../utils/get-flattened-cart-error-codes.js';
import { errorsMapping } from '../../mappers/errors-mapping.js';
import { SET_CUSTOMER_DETAILS } from '../../operations/mutations/set-customer-details.js';

const useSetCustomerDetails = ({ onError , onCompleted  } = {})=>{
    const dispatch = useDispatch();
    const [cart] = Hooks.useCartStorage();
    const { updateCacheWithCart  } = useUpdateCartCache();
    /**
   * Handle success and error response from mutation operation
   * @param responseData
   */ const handleOnCompleted = React__default.useCallback((responseData)=>{
        let result;
        if ('setCustomerDetails' in responseData) {
            var ref;
            result = responseData.setCustomerDetails;
            if ((ref = result.failures) === null || ref === void 0 ? void 0 : ref.length) {
                const errorCodes = getFlattenedCartFailureErrorCodes(result.failures);
                onError === null || onError === void 0 ? void 0 : onError({
                    reason: 'MUTATION'
                });
                /**
           *  Notify user about all failures
           *  To handle granular errors return from mutation operation
           */ errorCodes.forEach((error)=>toast.failed(error)
                );
            }
            if (result.success) {
                onCompleted === null || onCompleted === void 0 ? void 0 : onCompleted('MUTATION');
                dispatch(updateCartState({
                    cartState: result.checkoutState
                }));
            }
        }
    }, [
        dispatch,
        onCompleted,
        onError
    ]);
    const handleOnError = React__default.useCallback(()=>{
        onError === null || onError === void 0 ? void 0 : onError({
            reason: 'MUTATION'
        });
        // General error for apollo error response
        toast.failed(errorsMapping.SOMETHING_WENT_WRONG);
    }, [
        onError
    ]);
    const cartId = cartIdVar();
    const updateCache = React__default.useCallback((cache, { data  })=>{
        if (data && data.setCustomerDetails.success) {
            const updatedCart = data.setCustomerDetails.cart;
            updatedCart && updateCacheWithCart({
                cart: updatedCart,
                cache
            });
        }
    }, [
        updateCacheWithCart
    ]);
    const { mutationOptions  } = useMutationConfig({
        onError: handleOnError,
        onCompleted: handleOnCompleted
    });
    const [mutationCallback, mutation] = useMutation(SET_CUSTOMER_DETAILS, swcHelpers.objectSpread({}, mutationOptions, {
        update: updateCache,
        context: {
            forwardHeaders: {
                'cart-hash': cart.cartHash
            }
        }
    }));
    /**
   *  Customer details data update operation
   */ const setCustomerDetails = React__default.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(customerDetails) {
            if (!cartId) {
                throw new Error('Need to have valid cart to update!');
            }
            yield mutationCallback({
                variables: {
                    customerDetailsInput: swcHelpers.objectSpread({}, customerDetails, {
                        checkoutId: cartId
                    })
                }
            });
        });
        return function(customerDetails) {
            return _ref.apply(this, arguments);
        };
    }(), [
        cartId,
        mutationCallback
    ]);
    return {
        setCustomerDetails,
        isUpdating: mutation.loading,
        updateError: mutation.error
    };
};

export { useSetCustomerDetails };
//# sourceMappingURL=use-set-customer-details.js.map
